import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useRequest,
  type MutationFunction,
  type QueryFunction,
} from '../common';
import { getScheduledActionRequestListKey } from './keys';

//Fetches entire list of available scheduled action requests for a managed integration
export const useScheduledActionRequestList: QueryFunction<
  any[],
  [managedIntegrationUuid: string],
  ReturnType<typeof getScheduledActionRequestListKey>
> = (managedIntegrationUuid, { requestOptions, queryOptions, query } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getScheduledActionRequestListKey(managedIntegrationUuid),
    queryFn: ({ signal }) =>
      request(
        `/managed_integrations/${managedIntegrationUuid}/scheduled_integration_actions/`,
        {
          query,
          init: { signal },
        },
      ),
  });
};

//This create a scheduled action request, overwrites QueryData for that individual scheduled action request,
//and invalidates all related scheduled action request list queries
export const useCreateScheduledActionRequest: MutationFunction<
  unknown,
  { managedIntegrationUuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ managedIntegrationUuid, body }) => {
      return request(
        `/managed_integrations/${managedIntegrationUuid}/scheduled_integration_actions`,
        {
          query,
          init: { method: 'POST', body },
        },
      );
    },
    {
      ...queryOptions,
      mutationKey: ['createScheduledActionRequest'],
      onSuccess: (data, requestData, ...rest) => {
        queryClient.invalidateQueries({
          queryKey: getScheduledActionRequestListKey(
            requestData.managedIntegrationUuid,
          ),
        });
        queryOptions?.onSuccess?.(data, requestData, ...rest);
      },
    },
  );
};

//This deletes a scheduled action request, overwrites QueryData for that individual scheduled action request,
//and invalidates all related scheduled action request list queries
export const useDeleteScheduledActionRequest: MutationFunction<
  unknown,
  { scheduledActionRequestUuid: string; managedIntegrationUuid: string }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation(
    ({ scheduledActionRequestUuid }) => {
      return request(
        `/managed_integrations/scheduled_integration_actions/${scheduledActionRequestUuid}`,
        {
          query,
          init: { method: 'DELETE' },
        },
      );
    },
    {
      ...queryOptions,
      mutationKey: ['deleteScheduledActionRequest'],
      onSuccess: (data, requestData, ...rest) => {
        queryClient.invalidateQueries({
          queryKey: getScheduledActionRequestListKey(
            requestData.managedIntegrationUuid,
          ),
        });
        queryOptions?.onSuccess?.(data, requestData, ...rest);
      },
    },
  );
};

export default {
  useScheduledActionRequestList,
  useCreateScheduledActionRequest,
  useDeleteScheduledActionRequest,
};
